import { useState } from 'react'
import { Search, ChevronLeft, ChevronRight, Tag, Image, Trash2, X, Grid, List, Share2 } from 'lucide-react'

export default function ImageList({
  images,
  selected,
  loading,
  hasMore,
  onSelect,
  onSearch,
  onLoadMore,
  onSort,
  sortOrder,
  allTags,
  activeTag,
  onTagFilter,
  tagFilterImageIds,
  checked,
  onToggle,
  onToggleAll,
  onClearSelection,
  onBulkTag,
  onBulkDelete,
  bulkBusy
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState('grid') // 'grid' or 'list'
  const [copiedId, setCopiedId] = useState(null)

  function handleSearchSubmit(e) {
    e.preventDefault()
    onSearch(searchTerm)
  }

  function copyUrl(e, img) {
    e.stopPropagation()
    const url = img.variants?.find(v => v.endsWith('/public')) || img.variants?.[0] || ''
    if (!url) return
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(img.id)
      setTimeout(() => setCopiedId(null), 1500)
    })
  }

  // Apply tag filter client-side
  const displayImages = tagFilterImageIds !== null
    ? images.filter(img => tagFilterImageIds.includes(img.id))
    : images

  const allChecked = displayImages.length > 0 && displayImages.every(img => checked.has(img.id))
  const someChecked = checked.size > 0

  return (
    <>
      <div className="image-list-controls">
        <form className="image-list-search" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search by filename..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button type="submit">
            <Search size={16} />
          </button>
        </form>

        <div className="image-list-tag-filter">
          <Tag size={13} />
          <select
            value={activeTag}
            onChange={e => onTagFilter(e.target.value)}
          >
            <option value="">All Tags</option>
            {(allTags || []).map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div className="image-list-sort">
          <label>
            Sort:
            <select value={sortOrder} onChange={e => onSort(e.target.value)}>
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </label>
          <div className="view-toggle">
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Thumbnail view"
            >
              <Grid size={14} />
            </button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <List size={14} />
            </button>
          </div>
        </div>
      </div>

      {someChecked && (
        <div className="bulk-action-bar image-bulk-bar">
          <span className="bulk-count">{checked.size} selected</span>
          <button onClick={onBulkTag} disabled={bulkBusy}><Tag size={12} /> Tag</button>
          <button onClick={onBulkDelete} disabled={bulkBusy} className="danger"><Trash2 size={12} /> Delete</button>
          <button onClick={onClearSelection} className="bulk-clear"><X size={12} /> Clear</button>
        </div>
      )}

      <div className="image-list-grid">
        {loading && images.length === 0 ? (
          <div className="image-list-empty">Loading images...</div>
        ) : displayImages.length === 0 ? (
          <div className="image-list-empty">
            {activeTag ? `No images tagged "${activeTag}"` : 'No images found'}
          </div>
        ) : (
          <>
            <div className="image-list-header">
              <label className="image-select-all">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={() => onToggleAll(displayImages)}
                />
                Select all
              </label>
              <span className="image-count">{displayImages.length} image{displayImages.length !== 1 ? 's' : ''}</span>
            </div>

            {viewMode === 'grid' ? (
              <div className="image-grid">
                {displayImages.map(img => {
                  const thumbUrl = img.variants?.[0] || ''
                  const isSelected = selected?.id === img.id

                  return (
                    <div
                      key={img.id}
                      className={`image-card ${isSelected ? 'selected' : ''} ${checked.has(img.id) ? 'checked' : ''}`}
                    >
                      <div className="image-card-check" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checked.has(img.id)}
                          onChange={() => onToggle(img.id)}
                        />
                      </div>
                      <div className="image-card-thumb" onClick={() => onSelect(img.id)}>
                        {thumbUrl ? (
                          <img src={thumbUrl} alt={img.filename || img.id} loading="lazy" />
                        ) : (
                          <div className="image-card-placeholder"><Image size={32} /></div>
                        )}
                      </div>
                      <div className="image-card-info" onClick={() => onSelect(img.id)}>
                        <span className="image-card-name" title={img.filename}>{img.filename || img.id.substring(0, 12)}</span>
                        <span className="image-card-date">
                          {img.uploaded ? new Date(img.uploaded).toLocaleDateString() : ''}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="image-list-table">
                <table>
                  <thead>
                    <tr>
                      <th className="list-check-col"></th>
                      <th>Filename</th>
                      <th>Date</th>
                      <th className="list-share-col"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayImages.map(img => {
                      const isSelected = selected?.id === img.id
                      return (
                        <tr
                          key={img.id}
                          className={`${isSelected ? 'selected' : ''} ${checked.has(img.id) ? 'checked' : ''}`}
                        >
                          <td className="list-check-col" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={checked.has(img.id)}
                              onChange={() => onToggle(img.id)}
                            />
                          </td>
                          <td className="list-filename-col" onClick={() => onSelect(img.id)}>
                            {img.filename || img.id.substring(0, 20)}
                          </td>
                          <td className="list-date-col" onClick={() => onSelect(img.id)}>
                            {img.uploaded ? new Date(img.uploaded).toLocaleDateString() : '—'}
                          </td>
                          <td className="list-share-col">
                            <button
                              className={`share-btn ${copiedId === img.id ? 'copied' : ''}`}
                              onClick={e => copyUrl(e, img)}
                              title="Copy public URL"
                            >
                              {copiedId === img.id ? '✓' : <Share2 size={13} />}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {hasMore && (
              <div className="image-load-more">
                <button onClick={onLoadMore} disabled={loading}>
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
